import { DisplayText, Dropdown, Tippy } from '@superops/ui-design-system';
import React, { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { getTranslatedText, withErrorBoundary } from '@nile/components';
import { components } from 'react-select';
import { SVGIcon } from '@superops/super-icons';

const CustomOption = (props) => {
  const { data } = props;
  const intl = useIntl();
  return (
    <components.Option {...props}>
      <div className="flex actr w100 fx-sb tw-gap-x-2">
        <div>{data.label}</div>

        {!data.outgoingDomainVerified && (
          <div className="pointer">
            <Tippy
              title={getTranslatedText(
                intl,
                'ticket.details.mailbox.email.unverified'
              )}
              arrow
            >
              <SVGIcon name="InlineError" color="#ff0055" size="sm" />
            </Tippy>
          </div>
        )}
      </div>
    </components.Option>
  );
};

const TicketMailboxSelector = (props) => {
  const {
    onChange,
    value,
    placeholder,
    hideLabel,
    label,
    query,
    variables,
    client,
    formatOptions,
    populateDefaultMailbox,
    getDefaultMailbox,
    noBorder
  } = props;

  const [selectedMailbox, setSelectedMailbox] = useState(value || null);
  const [mailboxLoading, setMailboxLoading] = useState(false);
  const [mailboxes, setMailboxes] = useState([]);

  const intl = useIntl();

  const handleChange = (selectedOption) => {
    setSelectedMailbox(selectedOption);
    if (onChange) {
      onChange(selectedOption);
    }
  };

  const getMailboxes = async () => {
    if (client && query) {
      try {
        setMailboxLoading(true);
        const res = await client.query({
          query,
          variables
        });
        if (res?.data && formatOptions) {
          const options = formatOptions(res?.data);
          setMailboxes(options);
        }
        return res?.data;
      } catch (error) {
        console.log(error);
      } finally {
        setMailboxLoading(false);
      }
    }
  };

  const labelContent = label || getTranslatedText(intl, 'common.from');

  const placeHolderContent =
    placeholder ||
    getTranslatedText(intl, 'ticket.conversation.mailbox.select.placeholder');

  const populateDefaultMailboxData = async () => {
    const res = await getMailboxes();
    if (res?.data && getDefaultMailbox) {
      const defaultMailbox = getDefaultMailbox(res?.data);
      if (defaultMailbox) {
        handleChange(defaultMailbox);
      }
    }
  };

  useEffect(() => {
    if (populateDefaultMailbox && !value) {
      populateDefaultMailboxData();
    }
  }, []);

  return (
    <div className="flex tw-gap-x-2 w100 actr" id="mailbox-selector">
      {!hideLabel && (
        <DisplayText element="p" size="large">
          <span className="capitalize">{labelContent}: </span>
        </DisplayText>
      )}

      <div className="w100">
        <Tippy
          title={selectedMailbox?.outgoingAddress}
          arrow
          key={selectedMailbox?.outgoingAddress}
          interactive
        >
          <Dropdown
            noBorder={noBorder}
            value={selectedMailbox}
            options={mailboxes}
            onChange={handleChange}
            isLoading={mailboxLoading}
            onMenuOpen={getMailboxes}
            placeholder={placeHolderContent}
            isOptionDisabled={(option) => !option?.outgoingDomainVerified}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            components={{
              Option: CustomOption,
              IndicatorSeparator: () => null
            }}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 })
            }}
            noOptionsMessage={() => {
              return mailboxLoading
                ? 'Loading...'
                : getTranslatedText(intl, 'app.search.noOptions.pl');
            }}
          />
        </Tippy>
      </div>
    </div>
  );
};

export default withErrorBoundary(TicketMailboxSelector);
